import type { MoneyCents } from '../core/types';
import type { BonusState, PersistedStickyWild } from '../bonus/types';
import { decodeLegacyReplayV1, decodeReplayDraftV2 } from '../replay/decoder';
import { LEGACY_V1_LINE_DECODER } from '../replay/legacy-v1-lines';
import type { ReplayDraftV2, ReplayPresentationMode } from '../replay/schema';
import { MAX_REPLAY_STEPS } from '../replay/schema';
import { decodeExtremeRunState } from '../extreme/decoder';
import type { ExtremeRunStateV1 } from '../extreme/types';
import { parseJson, type StoragePort } from './storage';

export const SETTINGS_STORAGE_KEY = 'slot-yugioh-rework:v2:settings';
export const SESSION_STORAGE_KEY = 'slot-yugioh-rework:season3:v2:session';
export const LEGACY_SETTINGS_STORAGE_KEY = 'ygo_slot_settings_v1';
export const LEGACY_HOTKEY_STORAGE_KEY = 'ygo_slot_hotkeys_v1';
export const LEGACY_RUN_STORAGE_KEY = 'ygo_slot_run_v1';

const BET_CENTS = new Set([10, 20, 50, 100, 200, 500, 600, 700, 800, 900, 1_000, 2_000]);
const FORBIDDEN_HOTKEYS = new Set([
  'Enter', 'Tab', 'Escape', 'Shift', 'Control', 'Alt', 'Meta', 'PageUp', 'PageDown', 'Home', 'End',
]);

type UnknownRecord = Record<string, unknown>;

export interface HotkeySettings {
  readonly spin: string;
  readonly auto: string;
  readonly turbo: string;
}

export interface PersistedSettingsV2 {
  readonly version: 2;
  readonly betCents: MoneyCents;
  readonly turbo: boolean;
  readonly presentationMode: ReplayPresentationMode;
  readonly autoStopEnabled: boolean;
  readonly autoStopThresholdX: number;
  readonly masterVolume: number;
  readonly musicVolume: number;
  readonly effectsVolume: number;
  readonly masterMuted: boolean;
  readonly musicMuted: boolean;
  readonly effectsMuted: boolean;
  readonly hotkeys: HotkeySettings;
  readonly chatDisplayName: string;
  readonly chatLastReadAt: number;
}

export type PersistedBonusV2 = BonusState;

export interface PersistedRankedRunV2 {
  readonly id: string;
  readonly active: true;
  readonly name: string;
  readonly betCents: MoneyCents;
  readonly startBalanceCents: MoneyCents;
  readonly startedAt: number;
}

export interface PersistedSessionV2 {
  readonly version: 2;
  readonly revision: number;
  readonly savedAt: number;
  readonly balanceCents: MoneyCents;
  readonly betCents: MoneyCents;
  readonly rankedRun: PersistedRankedRunV2 | null;
  readonly extremeRun: ExtremeRunStateV1 | null;
  readonly bonus: PersistedBonusV2 | null;
  readonly replayDraft: ReplayDraftV2 | null;
}

export const DEFAULT_SETTINGS: PersistedSettingsV2 = Object.freeze({
  version: 2,
  betCents: 200,
  turbo: false,
  presentationMode: 'cards',
  autoStopEnabled: false,
  autoStopThresholdX: 50,
  masterVolume: 1,
  musicVolume: 0.84,
  effectsVolume: 1,
  masterMuted: false,
  musicMuted: false,
  effectsMuted: false,
  hotkeys: Object.freeze({ spin: ' ', auto: 'n', turbo: 'm' }),
  chatDisplayName: 'Duelist',
  chatLastReadAt: 0,
});

function record(value: unknown): UnknownRecord | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as UnknownRecord : null;
}

function integer(value: unknown, min = 0, max = Number.MAX_SAFE_INTEGER): number | null {
  return Number.isSafeInteger(value) && Number(value) >= min && Number(value) <= max ? Number(value) : null;
}

function finite(value: unknown, min = 0, max = Number.MAX_SAFE_INTEGER): number | null {
  const result = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(result) && result >= min && result <= max ? result : null;
}

function name(value: unknown, fallback = ''): string {
  if (typeof value !== 'string') return fallback;
  return value.replace(/\s+/g, ' ').trim().slice(0, 20) || fallback;
}

function hotkey(value: unknown): string | null {
  if (typeof value !== 'string' || value.length < 1 || value.length > 24 || FORBIDDEN_HOTKEYS.has(value)) return null;
  return value;
}

function decodeHotkeys(value: unknown): HotkeySettings | null {
  const source = record(value);
  if (!source) return null;
  const spin = hotkey(source.spin);
  const auto = hotkey(source.auto);
  const turbo = hotkey(source.turbo);
  if (!spin || !auto || !turbo || new Set([spin.toLowerCase(), auto.toLowerCase(), turbo.toLowerCase()]).size !== 3) return null;
  return { spin, auto, turbo };
}

function positions(value: unknown): readonly PersistedStickyWild[] | null {
  if (!Array.isArray(value) || value.length > 15) return null;
  const result = new Map<string, PersistedStickyWild>();
  for (const candidate of value) {
    const source = record(candidate);
    const reel = integer(source?.reel, 0, 4);
    const row = integer(source?.row, 0, 2);
    if (reel === null || row === null) return null;
    const multiplier = source?.multiplier === undefined ? undefined : integer(source.multiplier, 1);
    if (multiplier === null) return null;
    const position = { reel, row, ...(multiplier === undefined ? {} : { multiplier }) } as PersistedStickyWild;
    result.set(`${reel}:${row}`, position);
  }
  return [...result.values()].sort((left, right) => left.reel - right.reel || left.row - right.row);
}

export function decodeSettingsV2(value: unknown): PersistedSettingsV2 | null {
  const source = record(value);
  if (!source || source.version !== 2) return null;
  const betCents = integer(source.betCents, 1, 2_000);
  const autoStopThresholdX = finite(source.autoStopThresholdX, 1, 500_000);
  const masterVolume = finite(source.masterVolume, 0, 1);
  const musicVolume = finite(source.musicVolume, 0, 1);
  const effectsVolume = finite(source.effectsVolume, 0, 1);
  const decodedHotkeys = decodeHotkeys(source.hotkeys);
  const chatLastReadAt = integer(source.chatLastReadAt, 0);
  const presentationMode = source.presentationMode;
  if (betCents === null || !BET_CENTS.has(betCents) || autoStopThresholdX === null || masterVolume === null
    || musicVolume === null || effectsVolume === null || !decodedHotkeys || chatLastReadAt === null
    || (presentationMode !== 'cards' && presentationMode !== 'performance')
    || typeof source.turbo !== 'boolean' || typeof source.autoStopEnabled !== 'boolean'
    || typeof source.masterMuted !== 'boolean' || typeof source.musicMuted !== 'boolean'
    || typeof source.effectsMuted !== 'boolean') return null;
  return {
    version: 2, betCents, turbo: source.turbo, presentationMode: 'cards', autoStopEnabled: source.autoStopEnabled,
    autoStopThresholdX, masterVolume, musicVolume, effectsVolume, masterMuted: source.masterMuted,
    musicMuted: source.musicMuted, effectsMuted: source.effectsMuted, hotkeys: decodedHotkeys,
    chatDisplayName: name(source.chatDisplayName, 'Duelist'), chatLastReadAt,
  };
}

export function decodeLegacySettingsV1(value: unknown, hotkeyValue?: unknown): PersistedSettingsV2 | null {
  const source = record(value);
  if (!source) return null;
  const bet = finite(source.bet, 0.1, 20);
  const betCents = bet === null ? DEFAULT_SETTINGS.betCents : Math.round(bet * 100);
  if (!BET_CENTS.has(betCents)) return null;
  const percent = (candidate: unknown, fallback: number): number => {
    const decoded = finite(candidate, 0, 100);
    return decoded === null ? fallback : decoded / 100;
  };
  const legacyHotkeys = decodeHotkeys(hotkeyValue) ?? DEFAULT_SETTINGS.hotkeys;
  return {
    ...DEFAULT_SETTINGS,
    betCents,
    presentationMode: 'cards',
    autoStopEnabled: source.autoSpinStopEnabled === true,
    autoStopThresholdX: finite(source.autoSpinStopThreshold, 1, 500_000) ?? DEFAULT_SETTINGS.autoStopThresholdX,
    masterVolume: percent(source.masterVolume, DEFAULT_SETTINGS.masterVolume),
    musicVolume: percent(source.baseGameMusicVolume ?? source.baseMusicVolume, DEFAULT_SETTINGS.musicVolume),
    effectsVolume: (percent(source.drawSoundVolume, 1) + percent(source.shuffleSoundVolume, 1)) / 2,
    masterMuted: source.soundEnabled === false,
    musicMuted: source.baseGameMusicMuted === true,
    effectsMuted: source.drawSoundMuted === true && source.shuffleSoundMuted === true,
    hotkeys: legacyHotkeys,
    chatDisplayName: name(source.chatUsername, 'Duelist'),
    chatLastReadAt: Math.max(0, Math.round(finite(source.chatLastReadTs) ?? 0)),
  };
}

function decodeRankedRun(value: unknown): PersistedRankedRunV2 | null {
  if (value === null) return null;
  const source = record(value);
  if (!source || source.active !== true) return null;
  const id = typeof source.id === 'string' && /^[A-Za-z0-9_-]{1,128}$/.test(source.id) ? source.id : null;
  const betCents = integer(source.betCents, 1, 2_000);
  const startBalanceCents = integer(source.startBalanceCents, 0);
  const startedAt = integer(source.startedAt, 1);
  const decodedName = name(source.name);
  if (!id || betCents === null || !BET_CENTS.has(betCents) || startBalanceCents === null || startedAt === null || !decodedName) return null;
  return { id, active: true, name: decodedName, betCents, startBalanceCents, startedAt };
}

function decodeBonus(value: unknown): PersistedBonusV2 | null {
  if (value === null) return null;
  const source = record(value);
  if (!source || typeof source.active !== 'boolean') return null;
  const initialScatterCount = integer(source.initialScatterCount, 3, 5);
  const remainingSpins = integer(source.remainingSpins, 0, MAX_REPLAY_STEPS);
  const totalAwardedSpins = integer(source.totalAwardedSpins, 1, MAX_REPLAY_STEPS);
  const spinsPlayed = integer(source.spinsPlayed, 0, MAX_REPLAY_STEPS);
  const totalWinCents = integer(source.totalWinCents, 0);
  const retriggerCount = integer(source.retriggerCount, 0, 20);
  const raBoostCount = source.raBoostCount === undefined ? 0 : integer(source.raBoostCount, 0);
  const obeliskActivationCount = source.obeliskActivationCount === undefined
    ? 0
    : integer(source.obeliskActivationCount, 0);
  const stickyWilds = positions(source.stickyWilds);
  if (remainingSpins === null || totalAwardedSpins === null || spinsPlayed === null || totalWinCents === null
    || retriggerCount === null || raBoostCount === null || obeliskActivationCount === null
    || initialScatterCount === null || !stickyWilds || typeof source.pogTriggered !== 'boolean'
    || typeof source.started !== 'boolean' || remainingSpins + spinsPlayed !== totalAwardedSpins
    || (!source.active && remainingSpins !== 0)
    || (source.active && remainingSpins === 0)) return null;
  return {
    active: source.active, started: source.started, initialScatterCount: initialScatterCount as 3 | 4 | 5,
    remainingSpins, totalAwardedSpins, spinsPlayed, totalWinCents, stickyWilds,
    pogTriggered: source.pogTriggered, retriggerCount,
    ...(source.raBoostCount === undefined ? {} : { raBoostCount }),
    ...(source.obeliskActivationCount === undefined ? {} : { obeliskActivationCount }),
  };
}

export function decodeSessionV2(value: unknown): PersistedSessionV2 | null {
  const source = record(value);
  if (!source || source.version !== 2) return null;
  const revision = integer(source.revision, 0);
  const savedAt = integer(source.savedAt, 1);
  const balanceCents = integer(source.balanceCents, 0);
  const betCents = integer(source.betCents, 1, 2_000);
  const rankedRun = source.rankedRun === null ? null : decodeRankedRun(source.rankedRun);
  const rawExtremeRun = source.extremeRun ?? null;
  const extremeRun = rawExtremeRun === null ? null : decodeExtremeRunState(rawExtremeRun);
  const bonus = source.bonus === null ? null : decodeBonus(source.bonus);
  const replayDraft = source.replayDraft === null ? null : decodeReplayDraftV2(source.replayDraft);
  if (revision === null || savedAt === null || balanceCents === null || betCents === null || !BET_CENTS.has(betCents)
    || (source.rankedRun !== null && !rankedRun) || (rawExtremeRun !== null && !extremeRun)
    || (rankedRun !== null && extremeRun !== null) || (extremeRun !== null && betCents !== extremeRun.betCents)
    || (source.bonus !== null && !bonus)
    || (source.replayDraft !== null && !replayDraft) || (replayDraft && !bonus)) return null;
  return { version: 2, revision, savedAt, balanceCents, betCents, rankedRun, extremeRun, bonus, replayDraft };
}

export function decodeLegacyRunV1(value: unknown): PersistedSessionV2 | null {
  const source = record(value);
  if (!source || (source.active !== true && source.runActive !== true)) return null;
  const bet = finite(source.bet ?? source.selectedBet, 0.1, 20);
  const startBalance = finite(source.startBalance, 0);
  const currentBalance = finite(source.currentBalance, 0);
  const startedAt = finite(source.startedAt ?? source.runStartTime, 1);
  const decodedName = name(source.username);
  if (bet === null || startBalance === null || currentBalance === null || startedAt === null || !decodedName) return null;
  const betCents = Math.round(bet * 100);
  if (!BET_CENTS.has(betCents)) return null;
  const remaining = Math.max(0, Math.trunc(finite(source.freeSpinsLeft) ?? 0));
  const total = Math.max(remaining, Math.trunc(finite(source.freeSpinsTotal) ?? remaining));
  const played = Math.max(0, Math.trunc(finite(source.freeSpinsPlayed) ?? 0));
  const stickyWilds = positions(Array.isArray(source.stickyWilds) ? source.stickyWilds : []);
  if (!stickyWilds) return null;
  const hasLegacyBonus = source.isFreeSpinMode === true && (remaining > 0 || total > 0);
  const bonus: PersistedBonusV2 | null = hasLegacyBonus ? {
    active: remaining > 0, started: true, initialScatterCount: 3,
    remainingSpins: remaining, totalAwardedSpins: Math.max(1, total), spinsPlayed: played,
    totalWinCents: Math.round((finite(source.freeSpinSessionWin) ?? 0) * 100), stickyWilds,
    pogTriggered: source.pogTriggeredThisFeature === true,
    retriggerCount: Math.max(0, Math.trunc(finite(record(source.freeSpinReplaySession)?.retriggerCount) ?? 0)),
  } : null;
  let replayDraft: ReplayDraftV2 | null = null;
  const legacyDraft = record(source.freeSpinReplaySession);
  if (bonus && legacyDraft) {
    const legacyReplay = decodeLegacyReplayV1({
      ...legacyDraft,
      version: 1,
      clientEntryId: `legacy-draft-${Math.round(startedAt)}`,
      username: legacyDraft.username ?? decodedName,
      bet: legacyDraft.bet ?? bet,
      timestamp: legacyDraft.startedAt ?? startedAt,
      totalSpins: Array.isArray(legacyDraft.steps) ? legacyDraft.steps.length : undefined,
      finalStickyCount: stickyWilds.length,
    }, undefined, LEGACY_V1_LINE_DECODER);
    if (legacyReplay) {
      replayDraft = {
        version: 2, id: legacyReplay.id, name: legacyReplay.name, betCents: legacyReplay.betCents,
        startedAt: legacyReplay.createdAt, presentationMode: legacyReplay.presentationMode,
        totalAwarded: legacyReplay.totalAwarded, retriggerCount: legacyReplay.retriggerCount,
        steps: legacyReplay.steps,
      };
    }
  }
  return {
    version: 2, revision: 0, savedAt: Date.now(), balanceCents: Math.round(currentBalance * 100), betCents,
    rankedRun: {
      id: `legacy-run-${Math.round(startedAt)}`, active: true, name: decodedName, betCents,
      startBalanceCents: Math.round(startBalance * 100), startedAt: Math.round(startedAt),
    },
    extremeRun: null,
    bonus, replayDraft,
  };
}

export interface PersistenceStore {
  loadSettings(): PersistedSettingsV2;
  saveSettings(settings: PersistedSettingsV2): void;
  loadSession(): PersistedSessionV2 | null;
  saveSession(session: PersistedSessionV2): void;
  clearSession(): void;
  loadGameSnapshot(): PersistedSessionV2 | null;
  saveGameSnapshot(session: PersistedSessionV2): void;
}

export class PersistenceService implements PersistenceStore {
  constructor(private readonly storage: StoragePort) {}

  loadSettings(): PersistedSettingsV2 {
    const decoded = decodeSettingsV2(parseJson(this.storage.getItem(SETTINGS_STORAGE_KEY)));
    if (decoded) return decoded;
    const legacyRaw = parseJson(this.storage.getItem(LEGACY_SETTINGS_STORAGE_KEY));
    const legacy = decodeLegacySettingsV1(legacyRaw, parseJson(this.storage.getItem(LEGACY_HOTKEY_STORAGE_KEY)));
    if (legacy) { this.saveSettings(legacy); return legacy; }
    this.storage.removeItem(SETTINGS_STORAGE_KEY);
    return DEFAULT_SETTINGS;
  }

  saveSettings(settings: PersistedSettingsV2): void {
    const decoded = decodeSettingsV2(settings);
    if (!decoded) throw new TypeError('Refusing to persist invalid settings.');
    this.storage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(decoded));
  }

  loadSession(): PersistedSessionV2 | null {
    const decoded = decodeSessionV2(parseJson(this.storage.getItem(SESSION_STORAGE_KEY)));
    if (decoded) return decoded;
    const legacy = decodeLegacyRunV1(parseJson(this.storage.getItem(LEGACY_RUN_STORAGE_KEY)));
    if (legacy) { this.saveSession(legacy); return legacy; }
    this.storage.removeItem(SESSION_STORAGE_KEY);
    return null;
  }

  saveSession(session: PersistedSessionV2): void {
    const decoded = decodeSessionV2(session);
    if (!decoded) throw new TypeError('Refusing to persist an invalid session.');
    this.storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(decoded));
  }

  clearSession(): void { this.storage.removeItem(SESSION_STORAGE_KEY); }
  loadGameSnapshot(): PersistedSessionV2 | null { return this.loadSession(); }
  saveGameSnapshot(session: PersistedSessionV2): void { this.saveSession(session); }
}
